/**
 * Unit conversion and display formatting. The API speaks SI; every surface formats through
 * these helpers so the app, Studio and widgets always agree.
 */

import { fastClock, fastDate } from './tzfast'

/** The thin space this module puts before AM/PM (Intl's own spacing differs between runtimes). */
const THIN = '\u2009'

export type TempUnit = 'F' | 'C'
export type WindUnit = 'mph' | 'kmh' | 'ms' | 'kn'
export type PrecipUnit = 'in' | 'mm'
export type PressureUnit = 'inHg' | 'hPa' | 'mb'
export type DistanceUnit = 'mi' | 'km'

export interface Units {
  temp: TempUnit
  wind: WindUnit
  precip: PrecipUnit
  pressure: PressureUnit
  distance: DistanceUnit
  /** 12- or 24-hour clock. */
  clock: 12 | 24
}

export const US_UNITS: Units = {
  temp: 'F',
  wind: 'mph',
  precip: 'in',
  pressure: 'inHg',
  distance: 'mi',
  clock: 12,
}

export const METRIC_UNITS: Units = {
  temp: 'C',
  wind: 'kmh',
  precip: 'mm',
  pressure: 'hPa',
  distance: 'km',
  clock: 24,
}

// ─── Conversions ───────────────────────────────────────────────────────────────

export const cToF = (c: number) => (c * 9) / 5 + 32

export function temp(c: number | null | undefined, unit: TempUnit): number | null {
  if (c == null || !Number.isFinite(c)) return null
  return unit === 'F' ? cToF(c) : c
}

export function wind(ms: number | null | undefined, unit: WindUnit): number | null {
  if (ms == null || !Number.isFinite(ms)) return null
  switch (unit) {
    case 'mph':
      return ms * 2.236936
    case 'kmh':
      return ms * 3.6
    case 'kn':
      return ms * 1.943844
    default:
      return ms
  }
}

export function precip(mm: number | null | undefined, unit: PrecipUnit): number | null {
  if (mm == null || !Number.isFinite(mm)) return null
  return unit === 'in' ? mm / 25.4 : mm
}

export function snow(cm: number | null | undefined, unit: PrecipUnit): number | null {
  if (cm == null || !Number.isFinite(cm)) return null
  return unit === 'in' ? cm / 2.54 : cm
}

export function pressure(hpa: number | null | undefined, unit: PressureUnit): number | null {
  if (hpa == null || !Number.isFinite(hpa)) return null
  return unit === 'inHg' ? hpa * 0.0295299830714 : hpa
}

export function distance(m: number | null | undefined, unit: DistanceUnit): number | null {
  if (m == null || !Number.isFinite(m)) return null
  return unit === 'mi' ? m / 1609.344 : m / 1000
}

// ─── Formatting ────────────────────────────────────────────────────────────────

const DASH = '–'

/** "72°" — rounded, with a degree sign and no unit letter (Apple style). */
export function fmtTemp(c: number | null | undefined, unit: TempUnit): string {
  const v = temp(c, unit)
  return v == null ? DASH : `${Math.round(v)}°`
}

export function fmtWind(ms: number | null | undefined, unit: WindUnit): string {
  const v = wind(ms, unit)
  if (v == null) return DASH
  return `${Math.round(v)} ${windLabel(unit)}`
}

export function windLabel(unit: WindUnit): string {
  return unit === 'kmh' ? 'km/h' : unit === 'ms' ? 'm/s' : unit === 'kn' ? 'kn' : 'mph'
}

export function fmtPrecip(mm: number | null | undefined, unit: PrecipUnit): string {
  const v = precip(mm, unit)
  if (v == null) return DASH
  if (unit === 'in') return v > 0 && v < 0.01 ? '<0.01″' : `${v.toFixed(v >= 10 ? 1 : 2)}″`
  return v > 0 && v < 0.1 ? '<0.1 mm' : `${v.toFixed(v >= 10 ? 0 : 1)} mm`
}

export function fmtSnow(cm: number | null | undefined, unit: PrecipUnit): string {
  const v = snow(cm, unit)
  if (v == null) return DASH
  if (unit === 'in') return v > 0 && v < 0.1 ? '<0.1″' : `${v.toFixed(1)}″`
  return v > 0 && v < 0.5 ? '<0.5 cm' : `${v.toFixed(v >= 10 ? 0 : 1)} cm`
}

export function fmtPressure(hpa: number | null | undefined, unit: PressureUnit): string {
  const v = pressure(hpa, unit)
  if (v == null) return DASH
  return unit === 'inHg' ? `${v.toFixed(2)} inHg` : `${Math.round(v)} ${unit}`
}

export function fmtDistance(m: number | null | undefined, unit: DistanceUnit): string {
  const v = distance(m, unit)
  if (v == null) return DASH
  const rounded = v >= 10 ? Math.round(v) : Math.round(v * 10) / 10
  return `${rounded} ${unit}`
}

export function fmtPercent(pct: number | null | undefined): string {
  return pct == null || !Number.isFinite(pct) ? DASH : `${Math.round(pct)}%`
}

const COMPASS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']

/** 225 → "SW" (16-point compass). */
export function compass(deg: number | null | undefined): string {
  if (deg == null || !Number.isFinite(deg)) return DASH
  return COMPASS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16]
}

/**
 * "2 mi NE of Kingston" — where a point is relative to the nearest named place. Silent
 * (null) within a mile / 1.6 km, where the name alone is honest.
 */
export function fmtNear(near: { distance_km: number; bearing_deg: number } | null | undefined, name: string, unit: DistanceUnit): string | null {
  if (!near || near.distance_km < 1.6) return null
  return `${fmtDistance(near.distance_km * 1000, unit)} ${compass(near.bearing_deg)} of ${name}`
}

// ─── Time in the location's timezone ───────────────────────────────────────────

const fmtCache = new Map<string, Intl.DateTimeFormat>()

export function formatter(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = timeZone + JSON.stringify(options)
  let f = fmtCache.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone, ...options })
    fmtCache.set(key, f)
  }
  return f
}

/** "4 PM" / "16" — hour label for timelines. */
export function fmtHour(iso: string, timeZone: string, clock: 12 | 24 = 12): string {
  const d = new Date(iso)
  const fast = fastClock(d.getTime(), timeZone, clock, false, THIN)
  if (fast != null) return fast
  if (clock === 24) return formatter(timeZone, { hour: '2-digit', hourCycle: 'h23' }).format(d)
  return formatter(timeZone, { hour: 'numeric', hour12: true }).format(d).replace(' ', ' ')
}

/** "4:07 PM" / "16:07". */
export function fmtTime(iso: string | null | undefined, timeZone: string, clock: 12 | 24 = 12): string {
  if (!iso) return DASH
  const d = new Date(iso)
  const fast = fastClock(d.getTime(), timeZone, clock, true, THIN)
  if (fast != null) return fast
  if (clock === 24) {
    return formatter(timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)
  }
  return formatter(timeZone, { hour: 'numeric', minute: '2-digit', hour12: true })
    .format(d)
    .replace(' ', ' ')
}

/** "Mon" for a local YYYY-MM-DD date; "Today" when it is today at the location. */
export function fmtWeekday(date: string, timeZone: string, now = new Date()): string {
  if (date === localDate(now.toISOString(), timeZone)) return 'Today'
  const [y, mo, d] = date.split('-').map(Number)
  // Noon UTC keeps the calendar day stable in every US timezone.
  return formatter('UTC', { weekday: 'short' }).format(new Date(Date.UTC(y, mo - 1, d, 12)))
}

/** Local calendar date "YYYY-MM-DD" for an instant at the location. */
export function localDate(iso: string, timeZone: string): string {
  const fast = fastDate(new Date(iso).getTime(), timeZone)
  if (fast != null) return fast
  const parts = formatter(timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(
    new Date(iso),
  )
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** "3 min ago", "2 hr ago" — for provenance lines. */
export function fmtAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return DASH
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)} min ago`
  if (s < 86400) return `${Math.round(s / 3600)} hr ago`
  return `${Math.round(s / 86400)} d ago`
}
